'use client'

import SplashScreen from '@/components/ui/SplashScreen'
import TourWrapper from '@/components/ui/TourWrapper'
import { isSuccessResult } from '@/lib/utils'
import { Prisma } from '@prisma/client'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import getTeacher from '../actions/teacher/getTeacher'
type TeacherContextValue = {
  teacher: Prisma.TeacherGetPayload<{
    include: { settings: true; calendlyEventTypes: true }
  }> | null
  refresh: () => Promise<void>
}

const TeacherContext = createContext<TeacherContextValue | null>(null)

export const TeacherProvider = ({
  initialTeacher,
  children,
}: {
  initialTeacher: Prisma.TeacherGetPayload<{
    include: { settings: true; calendlyEventTypes: true }
  }>
  children: ReactNode
}) => {
  const [teacher, setTeacher] = useState<Prisma.TeacherGetPayload<{
    include: { settings: true; calendlyEventTypes: true }
  }> | null>(initialTeacher)
  const [isSplashVisible, setIsSplashVisible] = useState(true)

  // ✅ Fetch fresh teacher from API
  const refresh = async () => {
    try {
      const teacherResponse = await getTeacher()
      if (isSuccessResult(teacherResponse)) {
        setTeacher(teacherResponse.data)
      }
    } catch (err) {
      console.error('Failed to refresh teacher', err)
    }
  }

  useEffect(() => {
    let savedTheme = localStorage.getItem('theme')
    if (!savedTheme) {
      const prefersDark = window.matchMedia(
        '(prefers-color-scheme: dark)',
      ).matches
      savedTheme = prefersDark ? 'dark' : 'light'
    }
    document.body.classList.remove('light', 'dark')
    document.body.classList.add(savedTheme)
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsSplashVisible(false)
    }, 700)

    return () => clearTimeout(timeout)
  }, [])

  return (
    <TourWrapper>
      <TeacherContext.Provider value={{ teacher, refresh }}>
        {isSplashVisible || !teacher ? <SplashScreen /> : children}
      </TeacherContext.Provider>
    </TourWrapper>
  )
}

// Custom hook to use the TeacherContext
export const useTeacher = () => {
  const context = useContext(TeacherContext)
  if (!context) {
    throw new Error('useTeacher must be used within a TeacherProvider')
  }
  return context
}
